import React from 'react';
import { observer } from 'mobx-react';
import { Table, Spin } from 'antd';
import { ColumnProps } from 'antd/es/table';
import { useAuthStore, useNhlGamesStore } from '../../contexts/mobx';
import { useSearchParams } from '../../utils/use-search-params';
import { useNavigation } from '../../utils/use-navigation';
import { SearchParams } from '../../stores/nhl-game';
import { NhlGame } from '../../types/nhl-game';
import { CreateButton } from '../../components/action-buttons';
import { PageTopBar } from '../../components/page-top-bar';
import { PaginationBar } from '../../components/pagination/pagination-bar';
import { Tag } from '../../components/tag';
import { Text } from '../../utils/text';

const renderTitle = (_: unknown, record: NhlGame) => {
    const enTitle = record.title.en;
    const frTitle = record.title.fr;
    return (
        <div>
            {enTitle}
            <br />
            <span className="light_label">{frTitle}</span>
        </div>
    );
};

const renderIsActive = (_: unknown, record: NhlGame) => {
    return record.status === 'active' ? (
        <Tag variant="success">Active</Tag>
    ) : (
        <Tag variant="error">Not active</Tag>
    );
};

const renderStage = (_: unknown, record: NhlGame) => {
    return record.stage === 'regular_season' ? (
        <Tag variant="blue">Regular Season</Tag>
    ) : (
        <Tag variant="magenta">Playoff</Tag>
    );
};

export const NhlGamesPage = observer(() => {
    const store = useNhlGamesStore();
    const navigation = useNavigation();
    const authStore = useAuthStore();

    const { hasManagementAccess } = authStore;

    if (!hasManagementAccess) {
        navigation.goToHome();
    }
    const search = useSearchParams<SearchParams>();

    React.useEffect(() => {
        store.getAllGames(search.params);
    }, [store, search.params]);

    const columns: ColumnProps<NhlGame>[] = [
        {
            title: 'Title',
            key: 'title',
            width: '20%',
            render: renderTitle,
        },
        {
            title: 'Is active',
            key: 'is_active',
            render: renderIsActive,
            defaultSortOrder: 'ascend',
            sorter: {
                compare: (a, b) => {
                    return a.status.localeCompare(b.status);
                },
                multiple: 2,
            },
        },
        {
            title: 'Stage',
            key: 'stage',
            render: renderStage,
        },
        {
            title: 'Year',
            key: 'year',
            dataIndex: 'year',
            defaultSortOrder: 'descend',
            sorter: {
                compare: (a, b) => {
                    return a.year - b.year;
                },
                multiple: 1,
            },
        },
    ];

    const goToEditPage = (o: NhlGame) => () => {
        navigation.goToNhlGameEdit(o.id);
    };

    return (
        <>
            <PageTopBar title={Text.nhlGame.configContainer.pageTitle}>
                <CreateButton
                    onClick={() => {
                        navigation.goToNhlGameCreate();
                    }}
                >
                    {Text.nhlGame.configContainer.createButton}
                </CreateButton>
            </PageTopBar>

            <div className="action_zone_above_table">
                <PaginationBar
                    pagination={{
                        page: search.params.page,
                        pageSize: search.params.perPage,
                        total: store.list?.total,
                        onPageChange: (page) => search.setParams({ page }),
                        onPageSizeChange: (perPage) =>
                            search.setParams({ perPage, page: 0 }),
                    }}
                />
            </div>

            {store.isFetchingList ? (
                <Spin />
            ) : (
                <Table<NhlGame>
                    className="clickable_table_body_rows"
                    rowKey="id"
                    dataSource={store.list?.results}
                    columns={columns}
                    loading={store.isFetchingList}
                    pagination={false}
                    bordered
                    onRow={(r) => ({
                        onClick: goToEditPage(r),
                    })}
                />
            )}
        </>
    );
});
